import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const GOLD = '#C99316';
const NAVY = '#071B3A';
const IVORY = '#FAF8F3';

const ICON_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';

const QUOTES = [
  { text: "Leading with cultural intelligence is not about geography. It is about having the agility to lead anyone, anywhere.", author: "David Livermore" },
  { text: "The most important thing in communication is hearing what isn't said.", author: "Peter Drucker" },
  { text: "Culture is more often a source of conflict than of synergy. Differences are a nuisance at best — and often a disaster.", author: "Geert Hofstede" },
  { text: "To lead across cultures, you must first understand your own.", author: "Erin Meyer" },
  { text: "A different language is a different vision of life.", author: "Federico Fellini" },
  { text: "Cultural fluency is not the erasure of difference — it is the mastery of navigating it.", author: "Dr Promise Monday" },
  { text: "The third culture is not a compromise between two worlds. It is a new world, built at the intersection.", author: "Dr Promise Monday" },
  { text: "Identity is the foundation from which you lead. If it is unstable, your leadership will be too.", author: "Dr Promise Monday" },
  { text: "Trust must be built across cultures — it cannot be assumed.", author: "Francis Fukuyama" },
  { text: "Our ability to reach unity in diversity will be the beauty and the test of our civilisation.", author: "Mahatma Gandhi" },
  { text: "Silence is not the same in every culture. Neither is agreement. Neither is respect.", author: "Dr Promise Monday" },
  { text: "The biggest challenge in cross-cultural leadership is not what you don't know — it is what you assume you know.", author: "Andy Molinsky" },
  { text: "Culture shapes the lens through which we see everything — including ourselves.", author: "Fons Trompenaars" },
  { text: "What makes you effective in one cultural setting can make you ineffective in another.", author: "Andy Molinsky" },
  { text: "Belonging is not about fitting in. It is about being allowed to show up as yourself.", author: "Brené Brown" },
  { text: "Cultural fluency without compromise means knowing who you are well enough that adaptation never becomes loss.", author: "Dr Promise Monday" },
  { text: "You do not truly understand your own culture until you have stood outside it.", author: "Geert Hofstede" },
  { text: "Leadership and learning are indispensable to each other.", author: "John F. Kennedy" },
  { text: "We need to give each other the space to grow, to be ourselves, to exercise our diversity.", author: "Max de Pree" },
  { text: "Every assumption you hold about how work gets done is a cultural assumption.", author: "Erin Meyer" },
  { text: "You cannot understand a culture simply by reading about it. You must listen to the people who carry it.", author: "Dr Promise Monday" },
  { text: "Leadership is a conversation. The most skilled leaders know how to change languages mid-sentence.", author: "Dr Promise Monday" },
  { text: "In diversity there is beauty and there is strength.", author: "Maya Angelou" },
  { text: "The measure of a leader is the degree to which others grow in their presence.", author: "Max de Pree" },
  { text: "Trust across cultures is earned slowly and lost quickly. Build it with patience, protect it with consistency.", author: "Dr Promise Monday" },
  { text: "We are all multicultural, but we do not all have the skills to navigate it.", author: "Dr Promise Monday" },
  { text: "Leadership in a multicultural world begins with humility — the willingness to say: I do not know how things work here yet.", author: "Dr Promise Monday" },
  { text: "Communication works for those who work at it — even more so across cultures.", author: "John Powell" },
  { text: "To lead globally, you must first understand locally — starting with yourself.", author: "David Livermore" },
  { text: "The third culture child knows that the world is larger than any single map.", author: "Ruth Van Reken" },
  { text: "Every culture has genius within it. Cross-cultural leadership is the art of drawing it out.", author: "Dr Promise Monday" },
  { text: "You can't lead people you don't understand. And you can't understand people you won't listen to.", author: "Dr Promise Monday" },
  { text: "Cross-cultural leadership is not about tolerance. It is about genuine curiosity.", author: "Dr Promise Monday" },
  { text: "The deepest form of disrespect is to treat someone as if they were you.", author: "Dr Promise Monday" },
  { text: "Culture eats strategy for breakfast.", author: "Peter Drucker" },
  { text: "In a high-context culture, the meaning lives in the space between words.", author: "Edward T. Hall" },
  { text: "Leadership is not domination, but the art of persuading people to work toward a common goal.", author: "Daniel Goleman" },
  { text: "Management is doing things right. Leadership is doing the right things — across cultures, that demands wisdom.", author: "Peter Drucker" },
  { text: "The best leaders are those most willing to be disagreed with — and most skilled at understanding why.", author: "Dr Promise Monday" },
  { text: "When silence means concern and agreement means silence, leaders who cannot read the difference will always misread the room.", author: "Dr Promise Monday" },
  { text: "Leadership is not a solo performance. It is a cultural conversation — and you need to learn how to listen.", author: "Ronald Heifetz" },
  { text: "Culture does not change because we desire to change it. It changes when we organise ourselves differently.", author: "Frances Westley" },
  { text: "Third Culture Operating Language runs on kindness — truth clothed in respect, clarity without domination, accountability without humiliation.", author: "Dr Promise Monday" },
  { text: "Those who do not move, do not notice their chains.", author: "Rosa Luxemburg" },
  { text: "We are spiritual beings having a human experience — and that experience is always deeply cultural.", author: "Pierre Teilhard de Chardin" },
  { text: "When CQ is absent, leaders do not simply make mistakes. They misread people. They mislabel competence.", author: "Dr Promise Monday" },
  { text: "The room cannot become a third culture space by accident. A healthy space must be led, not merely hoped for.", author: "Dr Promise Monday" },
  { text: "Cultural intelligence is not a talent. It is a discipline. It can be learned, practised, and sustained.", author: "Dr Promise Monday" },
  { text: "The leader who says 'I treat everyone the same' has mistaken uniformity for fairness.", author: "Dr Promise Monday" },
  { text: "A multicultural team does not fail because people are difficult. It struggles because assumptions stay invisible — until they collide.", author: "Dr Promise Monday" },
];

const QuoteCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const visibleRef = useRef(true);

  // Auto-advance every 2 minutes
  useEffect(() => {
    const tick = () => {
      setVisible(false);
      setTimeout(() => {
        setCurrent(prev => (prev + 1) % QUOTES.length);
        setVisible(true);
      }, 700);
    };
    const timer = setInterval(tick, 120000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index) => {
    setVisible(false);
    setTimeout(() => {
      setCurrent(index);
      setVisible(true);
    }, 700);
  };

  const handlePrev = () => goTo((current - 1 + QUOTES.length) % QUOTES.length);
  const handleNext = () => goTo((current + 1) % QUOTES.length);

  const progressWidth = Math.round(((current + 1) / QUOTES.length) * 100) + '%';
  const quote = QUOTES[current];

  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: IVORY }}
      data-testid="quote-carousel-section"
    >
      {/* Subtle brand icon watermark */}
      <div className="tcl-quote-watermark">
        <img
          src={ICON_URL}
          alt=""
          aria-hidden="true"
          className="w-64 h-64 md:w-80 md:h-80 object-contain"
          style={{ opacity: 0.055 }}
        />
      </div>

      {/* Top gold rule */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.3) 30%, rgba(201,147,22,0.3) 70%, transparent)' }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-16 text-center">

        {/* Section label */}
        <span
          className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-12"
          style={{ color: GOLD + 'CC' }}
          data-testid="quote-section-label"
        >
          Perspectives on Leadership
        </span>

        {/* Quote content — fade transitions via CSS class */}
        <div
          className={visible ? 'tcl-quote-show' : 'tcl-quote-hide'}
          data-testid="quote-content"
        >
          {/* Opening quotation mark */}
          <span
            className="block font-serif leading-none mb-4 select-none"
            style={{ fontSize: '5rem', color: GOLD + '30', fontWeight: 300, lineHeight: 0.8 }}
            aria-hidden="true"
          >
            &ldquo;
          </span>

          <blockquote
            className="font-serif font-light tracking-tight leading-[1.3] mb-10"
            style={{ fontSize: 'clamp(1.35rem, 2.8vw, 2rem)', color: NAVY }}
          >
            {quote.text}
          </blockquote>

          <div className="flex items-center justify-center gap-5">
            <div style={{ width: '48px', height: '1px', backgroundColor: GOLD + '40' }} />
            <cite
              className="not-italic text-[11px] uppercase tracking-[0.22em] font-sans font-medium"
              style={{ color: NAVY + '59' }}
              data-testid="quote-author"
            >
              {quote.author}
            </cite>
            <div style={{ width: '48px', height: '1px', backgroundColor: GOLD + '40' }} />
          </div>
        </div>

        {/* Navigation row */}
        <div className="mt-14 flex items-center justify-center gap-6">
          <button
            onClick={handlePrev}
            className="p-2 transition-colors duration-200 hover:text-[#C99316]"
            style={{ color: NAVY + '40' }}
            data-testid="quote-prev"
            aria-label="Previous quote"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Progress bar */}
          <div
            className="relative h-0.5 flex-1 max-w-xs rounded-full"
            style={{ backgroundColor: NAVY + '12' }}
          >
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
              style={{ width: progressWidth, backgroundColor: GOLD }}
            />
          </div>

          <button
            onClick={handleNext}
            className="p-2 transition-colors duration-200 hover:text-[#C99316]"
            style={{ color: NAVY + '40' }}
            data-testid="quote-next"
            aria-label="Next quote"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Counter */}
        <p
          className="mt-4 font-sans text-[10px] uppercase tracking-[0.2em]"
          style={{ color: NAVY + '28' }}
          data-testid="quote-counter"
        >
          {current + 1} / {QUOTES.length}
        </p>
      </div>

      {/* Bottom gold rule */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.3) 30%, rgba(201,147,22,0.3) 70%, transparent)' }}
      />
    </section>
  );
};

export default QuoteCarousel;
